import { useState } from "react";
import { useNavigate } from "react-router-dom";

// Helper for color dots
function Dot({ color }) {
  return (
    <span className={`inline-block w-3 h-3 rounded-full mr-3 ${color}`}></span>
  );
}

function CardItem({
  openDialog,
  setUserId,
  color,
  icon,
  title,
  subtitle,
  data = [],
}) {
  const [isOpen, setIsOpen] = useState(false);
  const isAccordion = Array.isArray(data) && data.length > 0;

  const navigate = useNavigate();

  const handleClickUser = (userID) => {
    setUserId(userID);
    openDialog(true);
  };

  return (
    <div className="border border-[#d8d7dc] rounded mt-[16px]">
      <div
        className="h-[75px] p-[12px] flex items-center shadow-md rounded-t bg-white hover:bg-gray-100 cursor-pointer"
        onClick={() => {
          if (isAccordion) setIsOpen(!isOpen);
        }}
      >
        {icon}
        <Dot color={color} />
        <div>
          <div className="font-semibold text-slate-700">{title}</div>
          {subtitle && <div className="text-xs text-slate-400">{subtitle}</div>}
        </div>
      </div>

      {isAccordion && isOpen && (
        <div className="bg-gray-50 border-t text-sm max-h-[300px] overflow-y-auto text-slate-600">
          {data.map((item, idx) => {
            if (typeof item !== "object") {
              return (
                <div key={idx} className="px-4 py-2 border-b last:border-b-0">
                  {String(item)}
                </div>
              );
            }

            const {
              type,
              userID,
              locationID,
              startTime,
              endTime,
              breakStartTime,
              breakEndTime,
              status,
              reason,
            } = item;

            const formatTime = (str) => {
              const date = new Date(str);
              return date.toISOString().slice(0, 16).replace("T", " ");
            };

            const formatTimeShift = (str) => {
              const date = new Date(str);
              return date.toISOString().slice(10, 16).replace("T", " ");
            };

            const formatDate = (str) => {
              const date = new Date(str);
              return date.toISOString().slice(0, 10).replace("T", " ");
            };

            switch (type) {
              case "shift":
              case "shift_offer":
              case "shift_swap":
              case "open":
                return (
                  <div
                    onClick={() => navigate("/schedule")}
                    key={idx}
                    className="px-4 py-2 border-b cursor-pointer last:border-b-0"
                  >
                    <div className="font-semibold">{userID?.preferredName}</div>
                    <div className="text-xs text-gray-500">
                      {locationID?.name} ({locationID?.area})
                    </div>
                    <div className="text-xs font-semibold my-1">
                      <span>{formatDate(startTime)}</span>
                      {formatTimeShift(startTime)} - {formatTimeShift(endTime)}
                    </div>
                    {breakStartTime && breakEndTime && (
                      <div className="text-xs text-gray-400">
                        Break: {formatTimeShift(breakStartTime)} -{" "}
                        {formatTimeShift(breakEndTime)}
                      </div>
                    )}
                  </div>
                );

              case "shift_late":
                return (
                  <div
                    onClick={() => navigate("/schedule")}
                    key={idx}
                    className="px-4 py-2 border-b cursor-pointer last:border-b-0"
                  >
                    <div className="font-semibold">{userID?.preferredName}</div>

                    <div className="text-xs">{formatDate(startTime)}</div>
                    <div className="text-xs font-semibold my-1">
                      Start Time: {formatTimeShift(startTime)}
                    </div>
                  </div>
                );

              case "leave":
                const leaveStart = new Date(item.startTime);
                const leaveEnd = new Date(item.endTime);
                const durationHours = item.duration
                  ? item.duration.toFixed(1)
                  : "0";
                const statusColors = {
                  pending: "bg-orange-100 text-orange-800",
                  approved: "bg-green-100 text-green-800",
                  rejected: "bg-red-100 text-red-800",
                };
                const badgeColor =
                  statusColors[item.status] || "bg-gray-100 text-gray-600";
                const statusText = item.status
                  ? item.status.toUpperCase()
                  : "UNKNOWN";

                const formatUTC = (date) => {
                  const y = date.getUTCFullYear();
                  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
                  const d = String(date.getUTCDate()).padStart(2, "0");
                  const h = String(date.getUTCHours()).padStart(2, "0");
                  const min = String(date.getUTCMinutes()).padStart(2, "0");
                  return `${y}-${m}-${d} ${h}:${min}`;
                };

                return (
                  <div
                    key={idx}
                    className="px-4 py-2 border-b last:border-b-0 relative"
                  >
                    <div className="font-semibold text-gray-800">
                      <div
                        onClick={() => handleClickUser(item.userID.id)}
                        className="cursor-pointer"
                      >
                        {item.userID?.preferredName || "Unnamed User"}{" "}
                      </div>
                      <span className="text-sm text-gray-600">
                        ({durationHours} hours)
                      </span>
                    </div>

                    <div className="text-xs text-gray-500">
                      {item.leaveType}
                    </div>

                    <div className="text-xs text-gray-700 mt-1">
                      {formatUTC(leaveStart)} → {formatUTC(leaveEnd)}
                    </div>

                    <div
                      className={`absolute top-2 right-2 rounded-full px-2 py-0.5 text-xs font-semibold shadow-sm ${badgeColor}`}
                    >
                      {statusText}
                    </div>
                  </div>
                );

              case "unavailable":
                return (
                  <div key={idx} className="px-4 py-2 border-b last:border-b-0">
                    <div className="font-semibold">{userID?.preferredName}</div>
                    <div className="text-xs text-gray-500">{reason}</div>

                    <div className="text-xs mt-1">
                      {formatTime(startTime)} - {formatTime(endTime)}
                    </div>
                  </div>
                );

              default:
                return (
                  <div
                    key={idx}
                    className="px-4 py-2 border-b last:border-b-0 text-red-400"
                  >
                    Unknown type: {type}
                  </div>
                );
            }
          })}
        </div>
      )}
    </div>
  );
}

export default CardItem;
